import { useEffect, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { api, apiError } from "../../lib/api";

const EMPTY = { type: "puntoenvio", name: "", city: "", address: "", whatsapp: "", phone: "", hours: "", lat: null, lng: null, enabled: true, commission_pct: 0, code: "" };
const TYPES = [["boleteria", "Boletería"], ["agencia", "Agencia"], ["puntoenvio", "PuntoEnvío"]];

const Picker = ({ onPick }) => {
  useMapEvents({ click: (e) => onPick(e.latlng) });
  return null;
};

export const AgencyFormDialog = ({ open, agency, onClose, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setF(agency ? { ...EMPTY, ...agency } : EMPTY);
  }, [agency, open]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (f.lat == null) return toast.error("Tocá el mapa para ubicar el punto");
    setSaving(true);
    try {
      const { id, survey_status, survey_type, ...body } = f; // eslint-disable-line no-unused-vars
      body.commission_pct = Number(body.commission_pct) || 0;
      const { data } = agency?.id ? await api.put(`/admin/agencies/${agency.id}`, body) : await api.post("/admin/agencies", body);
      toast.success("Guardado");
      onSaved(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="agency-form-dialog" className="max-h-[92vh] max-w-2xl overflow-y-auto rounded-3xl" data-lenis-prevent>
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">{agency?.id ? "Editar punto" : "Nuevo punto"}</DialogTitle>
          <DialogDescription>Completá los datos y tocá el mapa para ubicarlo.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {TYPES.map(([v, l]) => (
              <button type="button" key={v} onClick={() => setF({ ...f, type: v })} data-testid={`agency-form-type-${v}`} className={`rounded-full px-4 py-2 text-sm font-bold ${f.type === v ? "bg-[var(--forest)] text-white" : "bg-[var(--paper)]"}`}>{l}</button>
            ))}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input data-testid="agency-form-name" placeholder="Nombre" required value={f.name} onChange={set("name")} className="h-11 rounded-xl" />
            <Input data-testid="agency-form-city" placeholder="Localidad" required value={f.city} onChange={set("city")} className="h-11 rounded-xl" />
          </div>
          <Input data-testid="agency-form-address" placeholder="Dirección" required value={f.address} onChange={set("address")} className="h-11 rounded-xl" />
          <div className="grid gap-3 sm:grid-cols-2">
            <Input data-testid="agency-form-whatsapp" placeholder="WhatsApp (ej. 11 5735-4437)" value={f.whatsapp || ""} onChange={set("whatsapp")} className="h-11 rounded-xl" />
            <Input data-testid="agency-form-phone" placeholder="Teléfono (opcional)" value={f.phone || ""} onChange={set("phone")} className="h-11 rounded-xl" />
          </div>
          <Input data-testid="agency-form-hours" placeholder="Horario (ej. Lun a Vie 8 a 20 · Sáb 9 a 13)" value={f.hours || ""} onChange={set("hours")} className="h-11 rounded-xl" />
          <div data-testid="agency-form-commerce" className="grid gap-3 rounded-2xl bg-[var(--paper)] p-3 sm:grid-cols-3">
            <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" data-testid="agency-form-enabled" checked={f.enabled !== false} onChange={(e) => setF({ ...f, enabled: e.target.checked })} className="h-5 w-5 accent-[var(--forest)]" /> Habilitada para vender</label>
            <Input data-testid="agency-form-commission" type="number" min="0" max="50" step="0.5" placeholder="Comisión %" value={f.commission_pct ?? 0} onChange={set("commission_pct")} className="h-11 rounded-xl" />
            <Input data-testid="agency-form-code" placeholder="Código web (ej. NYCMERLO)" value={f.code || ""} onChange={set("code")} className="h-11 rounded-xl uppercase" />
          </div>
          <div className="h-64 overflow-hidden rounded-2xl ring-1 ring-black/10" data-testid="agency-form-map">
            <MapContainer center={f.lat != null ? [f.lat, f.lng] : [-31, -62]} zoom={f.lat != null ? 13 : 4} className="h-full w-full">
              <TileLayer attribution="&copy; OpenStreetMap" url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <Picker onPick={({ lat, lng }) => setF((p) => ({ ...p, lat: +lat.toFixed(6), lng: +lng.toFixed(6) }))} />
              {f.lat != null && <CircleMarker center={[f.lat, f.lng]} radius={10} pathOptions={{ color: "#0B5323", fillColor: "#22FF00", fillOpacity: 1, weight: 3 }} />}
            </MapContainer>
          </div>
          <p className="font-mono text-xs text-[var(--muted-ink)]">{f.lat != null ? `Ubicación: ${f.lat}, ${f.lng}` : "Sin ubicación todavía"}</p>
          <button data-testid="agency-form-save" disabled={saving} className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[var(--forest)] font-bold text-white">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Guardar
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
